import _ from 'lodash'
import React, { useEffect, useState } from 'react'
import Layout from '~/component/common/Layout'
import * as api from '~/myApi'
import { SurveyDrawer } from '~/component/survey/SurveyForm'
import SurveyTable from '~/component/survey/SurveyTable'
import { Button, Stack } from '@mui/material'
import { CloudUpload } from '@mui/icons-material'
import queryString from 'query-string'
import ComfirmMessage from '~/component/common/ComfirmMessage'
import { Modal } from '~/component/common/Dialog'
import { SurveyFilter } from '~/component/survey/SurveyFilter'
import BulkUploadDialog from '~/component/survey/BulkUploadDialog'

import createMessages from '~/const/message'

import client from '~/utils/client'
import { useLocation, useNavigate, useParams, createSearchParams } from 'react-router-dom'

import {
  convertQueryoTypePreservingObject,
  convertQueryParamsToString,
  convertQueryParamsToNumber,
} from '~/utils/url';

type FilterProps = Pick<
  api.ServerFindSurveyFilter & api.ServerPagination,
  'name' | 'status' | 'page'
> & { id?: string };

export const SurveyList: React.FC<{}> = () => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const surveyMessage = createMessages()
  const [isSurveyFormModal, setIsSurveyFormModal] = useState<boolean>(false)
  const [surveyItems, setSurveyItems] = useState<api.ServerFindSurveysResponse | undefined>({})
  const [survey, setSurvey] = useState<api.TypesSurveyFullInfo>({})

  const [isSurveyFetching, setIsSurveyFetching] = useState<boolean>(false) // 単一サーベイ取得時fetch
  const [isSurveyItemsFetching, setIsSurveyItemsFetching] = useState<boolean>(false) // サーベイリスト取得時fetch
  const [isSurveyDeleteModal, setIsSurveyDeleteModal] = useState<boolean>(false) // 削除モーダル
  const [isCopyMode, setIsCopyMode] = useState<boolean>(false) // コピーモード
  const [isStatusModal, setIsStatusModal] = useState<boolean>(false) // 配信切り替えモーダル
  const [isBulkUploadModal, setIsBulkUploadModal] = useState<boolean>(false) // バルクアップロードモーダル

  const [filterValues, setFilterValues] = useState<FilterProps>({})

  const openDeleteConfirmModal = () => {
    setIsSurveyDeleteModal(true)
  }
  const deleteSurvey = () => {
    if (survey.id) {
      client.surveyAdminApiDeleteSurvey(survey.id)
        .then(() => {
          surveyMessage.deleteSuccess(survey?.name)
          setIsSurveyDeleteModal(false)
          setIsSurveyFormModal(false)
          fetchSurveyList()
        }).catch(({ error }) => {
          surveyMessage.error(error.message)
        })
    }
  }

  const changeStatus = () => {
    if (survey?.id) {
      client.surveyAdminApiUpdateSurveyStatus(
        survey.id,
        {
          'status': survey.status === api.EnumsSurveyStatus.SURVEY_STATUS_ON ? api.EnumsSurveyStatus.SURVEY_STATUS_OFF : api.EnumsSurveyStatus.SURVEY_STATUS_ON
        }
      ).then(() => {
        surveyMessage.success('ステータスの変更が完了しました')
        setIsStatusModal(false)
        fetchSurveyList()
      }).catch(({ error }) => {
        surveyMessage.error(error.message)
      })
    }
  }

  const fetchSurveyList = (params: FilterProps = filterValues) => {
    setIsSurveyItemsFetching(true)
    client.surveyAdminApiFindSurveys({
      'filter.ids': params.id ? [params.id] : undefined,
      'filter.name': params.name,
      'filter.status': params.status,
      'pagination.page': params.page,
      'pagination.perPage': 50,
      'sort.sortBy': 'SORT_BY_CREATED_AT',
      'sort.desc': true
    }, {}).then(({ data }) => {
      setSurveyItems(data)
    }).catch(({ error }) => {
      surveyMessage.error(error.message)
    }).finally(() => {
      setIsSurveyItemsFetching(false)
    })
  }
  const fetchSurvey = (id: string) => {
    setIsSurveyFetching(true)
    return client.surveyAdminApiGetSurvey(id).then(({ data }) => {
      if (data.survey) {
        setSurvey(data.survey)
      }
      return data
    }).catch(({ error }) => {
      surveyMessage.error(error.message)
    }).finally(() => {
      setIsSurveyFetching(false)
    })
  }

  useEffect(() => {
    const searchFilterObject = convertQueryoTypePreservingObject<FilterProps>(
      ['name', 'status', 'page', 'id'],
      {
        id: convertQueryParamsToString,
        page: convertQueryParamsToNumber,
        name: convertQueryParamsToString,
        status: v => v,
      }
    )

    setFilterValues(searchFilterObject)
    fetchSurveyList(searchFilterObject)
  }, [location.search])

  useEffect(() => {
    if (params.id) {
      fetchSurvey(params.id).then((data) => {
        if (data) {
          setIsSurveyFormModal(true)
        }
      })
    }
  }, [])

  const changeHash = (pathname: string, queries?: FilterProps) => {
    const id = queries?.id || filterValues!.id
    const page = queries?.page || filterValues!.page
    navigate({
      pathname: pathname,
      search: `?${createSearchParams(_.omitBy({
        id: id ? id!.toString() : '',
        page: page ? page!.toString() : '',
        name: queries?.name! || filterValues!.name!,
        status: (queries?.status || filterValues!.status) as string,
      }, (v) => !v))}`,
    })
  }

  const handleBulkUpload = async (surveys: api.ServerCreateSurveyRequest[]) => {
    const response = await client.surveyAdminApiBulkCreateSurveys({
      requests: surveys
    });
    if (response.data) {
      fetchSurveyList();
    }

    return response;
  };

  return (
    <Layout>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={() => {
          setSurvey({})
          setIsSurveyFormModal(!isSurveyFormModal)
        }}>
          サーベイの新規作成
        </Button>
        <Button
          variant="outlined"
          startIcon={<CloudUpload />}
          onClick={() => setIsBulkUploadModal(true)}
        >
          バルクアップロード
        </Button>
      </Stack>
      <SurveyFilter
        initialValues={filterValues}
      />
      <SurveyTable
        items={surveyItems?.surveys || []}
        loading={isSurveyItemsFetching}
        detailOnClick={(id) => {
          fetchSurvey(id)
          setIsSurveyFormModal(true)
        }}
        editOnClick={(id) => {
          fetchSurvey(id)
          changeHash(`/survey/${id}`)
          setIsSurveyFormModal(true)
        }}
        page={filterValues.page}
        changePageNum={(i) => {
          changeHash(`/`, {
            page: i,
          })
        }}
        total={parseFloat(surveyItems?.total || '')}
        {...{
          changeStatusConfirmModal: (survey: api.TypesSurveyFullInfo) => {
            setSurvey(survey)
            setIsStatusModal(true)
          }
        }}
        copyComplate={(id: string) => {
          surveyMessage.success(`ID（${id}）をコピーしました`)
        }}
      />
      <SurveyDrawer
        {...{
          title: survey.name || '',
          isOpen: isSurveyFormModal,
          initialValues: survey,
          isContentsLoading: isSurveyFetching,
          onClose: (isFetch) => {
            changeHash(`/`)
            setIsSurveyFormModal(!isSurveyFormModal)
            setIsCopyMode(false)
            if (isFetch) {
              fetchSurveyList()
            }
          },
          isCopyMode,
          changeCopyMode: () => setIsCopyMode(true),
          openDeleteConfirmModal
        }}
      />

      <Modal
        isOpen={isStatusModal}
        onClose={() => setIsStatusModal(false)}
        onSubmit={changeStatus}
        title={`${survey.name}ステータス変更`}
        submitButtonText="保存"
      >
        <ComfirmMessage {...{ message: `配信ステータスを「${survey.status === api.EnumsSurveyStatus.SURVEY_STATUS_ON ? 'OFF' : 'ON'}」に切り替えますがよろしいですか？` }} />
      </Modal>

      <Modal
        isOpen={isSurveyDeleteModal}
        onClose={() => setIsSurveyDeleteModal(false)}
        onSubmit={deleteSurvey}
        title={`${survey.name}削除`}
        submitButtonText="削除"
        className="delete"
      >
        <ComfirmMessage name={survey.name!} isDeleteComfirmMessage />
      </Modal>

      <BulkUploadDialog
        open={isBulkUploadModal}
        onClose={() => setIsBulkUploadModal(false)}
        onUpload={handleBulkUpload}
      />
    </Layout>
  )
}

export default SurveyList
